import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../services/api';

interface TradingPairCollection {
  id: string;
  symbol: string;
  exchangeName: string;
  exchangeMarketId: string;
  isActive: boolean;
  collectionTimeframes: string[];
  dataStatus: 'pending' | 'up_to_date' | 'outdated' | 'error';
  lastCollectedAt: string | null;
  lastSuccessfulCollection: string | null;
  totalDataPoints: number;
  errorCount: number;
  lastErrorMessage: string | null;
  earliestDataDate: string | null;
  latestDataDate: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CollectionStats {
  total_collections: number;
  active_collections: number;
  up_to_date_collections: number;
  outdated_collections: number;
  error_collections: number;
  total_data_points: number;
}

interface AvailablePair {
  symbol: string;
  base: string;
  quote: string;
  active: boolean;
  info: any;
}

const TradingPairDataCollectionPage: React.FC = () => {
  const [collections, setCollections] = useState<TradingPairCollection[]>([]);
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [availablePairs, setAvailablePairs] = useState<AvailablePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState('kraken');
  const [pairSearch, setPairSearch] = useState('');
  const [formData, setFormData] = useState({
    symbol: '',
    exchangeName: 'kraken',
    collectionTimeframes: ['1d', '1h']
  });
  const [triggeringCollection, setTriggeringCollection] = useState(false);

  // Filter available pairs based on search
  const filteredPairs = availablePairs.filter(pair => {
    if (!pairSearch) return true;
    const searchLower = pairSearch.toLowerCase();
    return pair.symbol.toLowerCase().includes(searchLower) ||
           pair.base.toLowerCase().includes(searchLower) ||
           pair.quote.toLowerCase().includes(searchLower);
  });

  const exchanges = [
    { id: 'kraken', name: 'Kraken', available: true },
    { id: 'binance', name: 'Binance', available: false },
    { id: 'coinbase', name: 'Coinbase', available: false }
  ];

  const timeframes = [
    { id: '1m', name: '1 Minute', description: 'High frequency (2 weeks retention)' },
    { id: '5m', name: '5 Minutes', description: 'Short term (1 month retention)' },
    { id: '15m', name: '15 Minutes', description: 'Intraday (3 months retention)' },
    { id: '1h', name: '1 Hour', description: 'Hourly (6 months retention)' },
    { id: '4h', name: '4 Hours', description: 'Medium term (1 year retention)' },
    { id: '1d', name: '1 Day', description: 'Daily (3 years retention)' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('[DATA_COLLECTION] Starting parallel API loading at:', new Date().toISOString());
      const startTime = Date.now();

      // PERFORMANCE OPTIMIZATION: Load collections and available pairs in parallel
      const [collectionsRes, availablePairsRes] = await Promise.allSettled([
        apiClient.get<{collections: TradingPairCollection[], stats: CollectionStats}>('/data-collection/trading-pairs'),
        apiClient.get<{tradingPairs: Record<string, {pairs: AvailablePair[], error?: string}>}>(`/exchange/trading-pairs?exchanges=${selectedExchange}&active=true`)
      ]);

      const loadTime = Date.now() - startTime;
      console.log(`[DATA_COLLECTION] Parallel API loading completed in ${loadTime}ms`);

      // Process collections response
      if (collectionsRes.status === 'fulfilled') {
        setCollections(collectionsRes.value.data?.collections || []);
        setStats(collectionsRes.value.data?.stats || null);
        console.log('[DATA_COLLECTION] Collections loaded successfully');
      } else {
        console.error('[DATA_COLLECTION] Failed to load collections:', collectionsRes.reason);
        toast.error('Failed to load data collections');
      }

      // Process available pairs response
      if (availablePairsRes.status === 'fulfilled') {
        const responseData = availablePairsRes.value.data; // Access data from ApiResponse
        const exchangeData = responseData?.tradingPairs?.[selectedExchange];
        
        if (exchangeData && !exchangeData.error) {
          // Convert to expected format for UI compatibility
          const formattedPairs = (exchangeData.pairs || []).map((pair: any) => ({
            symbol: pair.symbol,
            base: pair.baseAsset || pair.base,
            quote: pair.quoteAsset || pair.quote,
            active: pair.isActive !== undefined ? pair.isActive : pair.active !== false,
            info: pair
          }));
          
          setAvailablePairs(formattedPairs);
          console.log(`[DATA_COLLECTION] Loaded ${formattedPairs.length} trading pairs for ${selectedExchange}`);
        } else {
          const errorMsg = exchangeData?.error || 'No data available for selected exchange';
          console.error('[DATA_COLLECTION] Failed to load trading pairs:', errorMsg);
          console.log('[DATA_COLLECTION] Response data structure:', responseData);
          toast.error(`Failed to load trading pairs: ${errorMsg}`);
          setAvailablePairs([]);
        }
      } else {
        console.error('[DATA_COLLECTION] Failed to load available pairs:', availablePairsRes.reason);
        toast.error('Failed to load available trading pairs');
        setAvailablePairs([]);
      }

      console.log(`[DATA_COLLECTION] Page fully loaded in ${loadTime}ms with parallel API execution`);
    } catch (error: any) {
      console.error('[DATA_COLLECTION] Error loading data:', error);
      toast.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{collections: TradingPairCollection[], stats: CollectionStats}>('/data-collection/trading-pairs');
      setCollections(response.data?.collections || []);
      setStats(response.data?.stats || null);
    } catch (error: any) {
      console.error('Failed to load collections:', error);
      toast.error('Failed to load data collections');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePairs = async () => {
    try {
      const response = await apiClient.get<{tradingPairs: Record<string, {pairs: AvailablePair[], error?: string}>}>(`/exchange/trading-pairs?exchanges=${selectedExchange}&active=true`);
      const responseData = response.data; // Access data from ApiResponse
      const exchangeData = responseData?.tradingPairs?.[selectedExchange];
      
      if (exchangeData && !exchangeData.error) {
        // Convert to expected format for UI compatibility
        const formattedPairs = (exchangeData.pairs || []).map((pair: any) => ({
          symbol: pair.symbol,
          base: pair.baseAsset || pair.base,
          quote: pair.quoteAsset || pair.quote,
          active: pair.isActive !== undefined ? pair.isActive : pair.active !== false,
          info: pair
        }));
        
        setAvailablePairs(formattedPairs);
        console.log(`✅ Loaded ${formattedPairs.length} trading pairs for ${selectedExchange}`);
      } else {
        const errorMsg = exchangeData?.error || 'No data available for selected exchange';
        console.error('Failed to load trading pairs:', errorMsg);
        console.log('Response data structure:', responseData);
        toast.error(`Failed to load trading pairs: ${errorMsg}`);
        setAvailablePairs([]);
      }
    } catch (error: any) {
      console.error('Failed to load available pairs:', error);
      toast.error('Failed to load available trading pairs');
      setAvailablePairs([]);
    }
  };

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.symbol || formData.collectionTimeframes.length === 0) {
      toast.error('Please select a trading pair and at least one timeframe');
      return;
    }

    try {
      await apiClient.post<{message: string, collection: any}>('/data-collection/trading-pairs', formData);
      toast.success('Data collection created successfully! Initial data collection has started.');
      setShowAddForm(false);
      setFormData({
        symbol: '',
        exchangeName: 'kraken',
        collectionTimeframes: ['1d', '1h']
      });
      loadCollections();
    } catch (error: any) {
      console.error('Failed to create collection:', error);
      toast.error(error.response?.data?.message || 'Failed to create data collection');
    }
  };

  const handleToggleActive = async (collection: TradingPairCollection) => {
    try {
      await apiClient.patch<{message: string, collection: any}>(`/data-collection/trading-pairs/${collection.id}`, {
        isActive: !collection.isActive
      });
      
      toast.success(`Data collection ${!collection.isActive ? 'enabled' : 'disabled'} for ${collection.symbol}`);
      loadCollections();
    } catch (error: any) {
      console.error('Failed to toggle collection:', error);
      toast.error('Failed to update collection status');
    }
  };

  const handleDeleteCollection = async (collection: TradingPairCollection) => {
    if (!confirm(`Are you sure you want to delete data collection for ${collection.symbol}? This will also delete all historical data.`)) {
      return;
    }

    try {
      await apiClient.delete<{message: string, deletedHistoricalRecords: number}>(`/data-collection/trading-pairs/${collection.id}`);
      toast.success(`Data collection deleted for ${collection.symbol}`);
      loadCollections();
    } catch (error: any) {
      console.error('Failed to delete collection:', error);
      toast.error('Failed to delete data collection');
    }
  };

  const handleManualTrigger = async () => {
    const activeCollections = collections.filter(c => c.isActive);
    if (!confirm(`Trigger manual data collection for all ${activeCollections.length} active trading pairs? This will collect the latest data from exchanges.`)) {
      return;
    }

    try {
      setTriggeringCollection(true);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Trigger collection for each active trading pair
      for (const collection of activeCollections) {
        try {
          const endDate = new Date().toISOString();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - 7); // Last 7 days
          
          // For each timeframe in the collection
          for (const timeframe of collection.collectionTimeframes) {
            await apiClient.post<{message: string}>('/data-collector/historical', {
              action: 'manual_collection',
              symbol: collection.symbol,
              exchangeName: collection.exchangeName,
              timeframe: timeframe,
              startDate: startDate.toISOString(),
              endDate: endDate
            });
          }
          
          successCount++;
          console.log(`✅ Triggered collection for ${collection.symbol}`);
          
        } catch (collectionError) {
          console.error(`❌ Failed to trigger ${collection.symbol}:`, collectionError);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Manual data collection triggered for ${successCount} trading pairs! Check back in a few minutes for updated data.`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to trigger collection for ${errorCount} trading pairs. Check console for details.`);
      }
      
      // Refresh collections after a short delay to show any immediate updates
      setTimeout(() => {
        loadCollections();
      }, 3000);
      
    } catch (error: any) {
      console.error('Failed to trigger collection:', error);
      toast.error('Failed to trigger manual data collection');
    } finally {
      setTriggeringCollection(false);
    }
  };

  const handleSingleTrigger = async (collection: TradingPairCollection) => {
    if (!confirm(`Trigger manual data collection for ${collection.symbol}? This will collect the latest data from ${collection.exchangeName}.`)) {
      return;
    }

    try {
      const endDate = new Date().toISOString();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7); // Last 7 days
      
      let successCount = 0;
      let errorCount = 0;
      
      // Trigger collection for each timeframe
      for (const timeframe of collection.collectionTimeframes) {
        try {
          await apiClient.post<{message: string}>('/data-collector/historical', {
            action: 'manual_collection',
            symbol: collection.symbol,
            exchangeName: collection.exchangeName,
            timeframe: timeframe,
            startDate: startDate.toISOString(),
            endDate: endDate
          });
          
          successCount++;
          console.log(`✅ Triggered ${collection.symbol}/${timeframe}`);
          
        } catch (timeframeError) {
          console.error(`❌ Failed to trigger ${collection.symbol}/${timeframe}:`, timeframeError);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Manual data collection triggered for ${collection.symbol} (${successCount} timeframes)! Check back in a few minutes for updated data.`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to trigger ${errorCount} timeframes for ${collection.symbol}. Check console for details.`);
      }
      
      // Refresh collections after a short delay
      setTimeout(() => {
        loadCollections();
      }, 3000);
      
    } catch (error: any) {
      console.error('Failed to trigger single collection:', error);
      toast.error(error.response?.data?.message || 'Failed to trigger data collection');
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    switch (status) {
      case 'up_to_date':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'outdated':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'error':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'pending':
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'up_to_date': return 'Up to Date';
      case 'outdated': return 'Outdated';
      case 'error': return 'Error';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Trading Pair Data Collection
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage historical data collection for backtesting. Only trading pairs with collected data can be used for backtesting.
          </p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.total_collections}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Collections</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.active_collections}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.up_to_date_collections}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Up to Date</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.outdated_collections}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Outdated</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.error_collections}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Errors</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {(stats.total_data_points ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Data Points</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex flex-wrap gap-4">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            {showAddForm ? 'Cancel' : 'Add Trading Pair Collection'}
          </button>
          
          <button
            onClick={handleManualTrigger}
            disabled={triggeringCollection || collections.filter(c => c.isActive).length === 0}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              triggeringCollection || collections.filter(c => c.isActive).length === 0
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {triggeringCollection ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Triggering...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Manual Trigger</span>
              </>
            )}
          </button>
          
          {collections.filter(c => c.isActive).length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
              No active collections to trigger
            </div>
          )}
        </div>

        {/* Add Collection Form */}
        {showAddForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Add New Data Collection
            </h2>
            
            <form onSubmit={handleCreateCollection} className="space-y-4">
              {/* Exchange Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Exchange
                </label>
                <select
                  value={selectedExchange}
                  onChange={(e) => {
                    setSelectedExchange(e.target.value);
                    setFormData({ ...formData, exchangeName: e.target.value });
                    loadAvailablePairs();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {exchanges.map(exchange => (
                    <option key={exchange.id} value={exchange.id} disabled={!exchange.available}>
                      {exchange.name} {!exchange.available && '(Coming Soon)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trading Pair Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Trading Pairs
                </label>
                <input
                  type="text"
                  placeholder="Search by symbol, base or quote currency..."
                  value={pairSearch}
                  onChange={(e) => setPairSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {pairSearch ? `${filteredPairs.length} pairs match your search` : `${availablePairs.length} total pairs available`}
                </p>
              </div>

              {/* Trading Pair Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trading Pair
                </label>
                <select
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a trading pair...</option>
                  {filteredPairs.map(pair => (
                    <option key={pair.symbol} value={pair.symbol}>
                      {pair.symbol} ({pair.base}/{pair.quote})
                    </option>
                  ))}
                </select>
                {filteredPairs.length === 0 && pairSearch && (
                  <p className="mt-1 text-sm text-red-500 dark:text-red-400">
                    No trading pairs match your search. Try a different search term.
                  </p>
                )}
                {!pairSearch && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    All {availablePairs.length} trading pairs are available for selection
                  </p>
                )}
              </div>

              {/* Timeframes Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Collection Timeframes
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {timeframes.map(timeframe => (
                    <label key={timeframe.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.collectionTimeframes.includes(timeframe.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              collectionTimeframes: [...formData.collectionTimeframes, timeframe.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              collectionTimeframes: formData.collectionTimeframes.filter(tf => tf !== timeframe.id)
                            });
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {timeframe.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {timeframe.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Create Collection
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Collections Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Data Collections ({collections.length})
            </h2>
          </div>

          {collections.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Data Collections
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Start by adding a trading pair to collect historical data for backtesting.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Add Your First Collection
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Trading Pair
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Data Points
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Timeframes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Collection
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {collections.map((collection) => (
                    <tr key={collection.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {collection.symbol}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {collection.exchangeName.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={getStatusBadge(collection.dataStatus)}>
                            {getStatusText(collection.dataStatus)}
                          </span>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={collection.isActive}
                              onChange={() => handleToggleActive(collection)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                              {collection.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {collection.totalDataPoints.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {collection.collectionTimeframes.map(tf => (
                            <span key={tf} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {tf}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(collection.lastSuccessfulCollection)}
                        {collection.errorCount > 0 && (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            {collection.errorCount} errors
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          {collection.isActive && (
                            <button
                              onClick={() => handleSingleTrigger(collection)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 flex items-center space-x-1"
                              title="Trigger data collection for this pair"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              <span>Trigger</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCollection(collection)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Manual Trigger Information */}
        <div className="mt-8 bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
            🔄 Manual Data Collection
          </h3>
          <div className="text-sm text-green-800 dark:text-green-300 space-y-1">
            <p>• <strong>On-Demand Collection:</strong> Trigger immediate data collection for individual or all active trading pairs</p>
            <p>• <strong>Data Range:</strong> Fetches the last 7 days of market data from exchanges for all configured timeframes</p>
            <p>• <strong>Automatic Scheduling:</strong> Normal collection runs daily at 2 AM UTC, weekly backfill on Sundays</p>
            <p>• <strong>API Endpoint:</strong> Uses existing `/data-collector/historical` endpoint with manual_collection action</p>
            <p>• <strong>Use Cases:</strong> Before backtesting, after market events, or when data appears outdated</p>
          </div>
        </div>

        {/* Cost Information */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
            💰 Cost Optimization
          </h3>
          <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <p>• <strong>Selective Collection:</strong> Only collect data for 1-5 trading pairs to minimize costs (~€15/month)</p>
            <p>• <strong>Retention Policy:</strong> Daily data (3 years), Hourly data (6 months), Minute data (2 weeks)</p>
            <p>• <strong>Active Management:</strong> Disable unused collections to reduce storage costs</p>
            <p>• <strong>No Backup Costs:</strong> Data can be re-collected from exchanges if needed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPairDataCollectionPage; 